import * as React from 'react';
import Image from 'next/image';
import Section from 'react-scale/components/elements/section';
import marble from 'react-scale/public/static/summit/marble.jpg';
import { gsap } from 'react-scale/lib/gsap';
import * as Scrollytelling from '@bsmnt/scrollytelling';

import clsx from 'clsx';
import { useMedia } from 'react-scale/hooks/use-media';

const [BIGGEST_SIZE, SMALLEST_SIZE, MAX_OPACITY, MIN_OPACITY, TOTAL] = [726, 280, 1, 0.4, 6];
const MAX_PX_SIZES = [730, 300];

type BuilderImage = {
  src: string;
  alt?: string;
};

type SliderProps = {
  images: BuilderImage[];
};

export const Slider = ({ images = [] }: SliderProps) => {
  const [nextImageIndex, setNextImgIndex] = React.useState(1);
  const [pointerEvents, setPointerEvents] = React.useState(true);
  const maxIndex = React.useMemo(() => images.length, [images]);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const isMobile = useMedia('(max-width: 1023px)');

  const handleSwap = (offset = 1) => {
    if (!pointerEvents) return;

    setNextImgIndex(counter => (counter + offset) % maxIndex);
    setPointerEvents(false);
  };

  React.useEffect(() => {
    const interval = setInterval(handleSwap, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [nextImageIndex, handleSwap]);

  React.useEffect(() => {
    if (!containerRef.current || isMobile) return;

    gsap.to(containerRef.current, {
      autoAlpha: 1,
      delay: 1
    });
  }, [isMobile]);

  return (
    <Section relative>
      <Image
        src={marble}
        alt=""
        style={{ WebkitMaskImage: 'linear-gradient(transparent, black, black, transparent)' }}
        sizes="100vw"
        className="hidden lg:block w-screen h-[120%] object-cover absolute left-0 -top-[100px] opacity-75"
        quality={100}
      />
      <div
        ref={containerRef}
        className={clsx(
          'relative w-full flex flex-col items-center mb-20 lg:mb-0 lg:h-[750px]',
          isMobile || 'opacity-0'
        )}
      >
        {isMobile ? (
          <MobileSlider images={images} />
        ) : (
          Array(TOTAL)
            .fill('')
            .map((_, idx) => {
              const current = (nextImageIndex - 1 + idx) % maxIndex;
              const next = (nextImageIndex + idx) % maxIndex;

              return (
                <HorizontalSlide
                  containerWidth={containerRef.current?.offsetWidth}
                  key={idx}
                  index={idx}
                  currentImg={images.at(current)}
                  nextImg={images.at(next)}
                  animationCallback={() => setPointerEvents(true)}
                  handleClick={() => handleSwap()}
                />
              );
            })
        )}
      </div>
    </Section>
  );
};

const getTweenTimes = (
  totalStart: number,
  totalEnd: number,
  partialStart: number,
  partialEnd: number
) => {
  const start = (totalEnd - totalStart) * (partialStart / 100) + totalStart;
  const end = Math.min((totalEnd - totalStart) * (partialEnd / 100) + totalStart, 100);
  return { start, end };
};

const MobileSlider = ({ images }: SliderProps) => {
  return (
    <Scrollytelling.Root scrub={0.2}>
      <div className="w-full" style={{ height: 500 * images.length }}>
        <Scrollytelling.Pin
          childClassName="flex items-center"
          childHeight="100vh"
          pinSpacerHeight="100%"
        >
          {images.map(({ src, alt }, idx) => {
            const fraction = 100 / images.length;
            const start = fraction * idx;
            const end = Math.min(fraction + fraction * idx, 100);

            const tween = [];
            if (idx) {
              tween.push({
                to: {
                  scale: 1,
                  y: 0
                },
                // The negative start overlaps the current animation with the previous card fadeout
                ...getTweenTimes(start, end, -50, 20)
              });
            }
            if (idx !== images.length - 1) {
              tween.push({
                to: {
                  autoAlpha: 0,
                  y: -400
                },
                ...getTweenTimes(start, end, 35, 100)
              });
            }

            return (
              <Scrollytelling.Animation tween={tween}>
                <figure
                  key={idx}
                  style={{
                    zIndex: 10 - idx,
                    transformOrigin: 'bottom',
                    transform: `scale(calc(1 - 0.05 * ${idx})) translateY(calc(25px * ${idx}))`
                  }}
                  className={clsx(
                    idx ? 'absolute' : 'relative',
                    'w-full h-screen max-h-[500px] rounded-lg border border-[#FFFFFF66] overflow-hidden'
                  )}
                >
                  <Image
                    src={src}
                    alt={alt ?? `card-${idx}`}
                    fill
                    sizes="90vw"
                    className="object-cover"
                  />
                </figure>
              </Scrollytelling.Animation>
            );
          })}
        </Scrollytelling.Pin>
      </div>
    </Scrollytelling.Root>
  );
};

type AnimatedFigureProps = {
  index: number;
  nextImg: BuilderImage;
  currentImg: BuilderImage;
  animationCallback: VoidFunction;
  handleClick?: React.MouseEventHandler;
  containerWidth?: number;
};

const HorizontalSlide = ({
  index,
  nextImg,
  currentImg,
  animationCallback,
  handleClick,
  containerWidth
}: AnimatedFigureProps) => {
  const maxSizes = MAX_PX_SIZES[0] - ((MAX_PX_SIZES[0] - MAX_PX_SIZES[1]) * index) / (TOTAL - 1);
  const sizes = BIGGEST_SIZE - ((BIGGEST_SIZE - SMALLEST_SIZE) * index) / (TOTAL - 1);
  const vwSizes = (sizes / 1920) * 100 + 'vw';
  const opacity = MAX_OPACITY - ((MAX_OPACITY - MIN_OPACITY) * index) / (TOTAL - 1);
  const [hasRendered, setHasRendered] = React.useState(false);
  const containerRef = React.useRef<HTMLElement>(null);
  const [currentPosition, setCurrentPos] = React.useState(1);

  let positions: React.CSSProperties = {};
  if (index === 0) {
    positions.left = 0;
  } else if (index === TOTAL - 1) {
    positions.right = 0;
  } else {
    const largest = ((BIGGEST_SIZE / 1920) * 100) / 2;
    const smallest = ((SMALLEST_SIZE / 1920) * 100) / 2;
    const limits = `calc(${containerWidth}px - ${largest + smallest + 'vw'})`;
    positions.left = `calc(${index} * (${limits} / ${TOTAL - 1}) - ${vwSizes} / 2 + ${largest}vw)`;
  }

  React.useEffect(() => {
    if (!containerRef.current || !hasRendered) return;

    const selectors = ['img.prev-slide', 'img.current-slide', 'img.next-slide'];
    const hiddenSlide = containerRef.current.querySelector(selectors.at(currentPosition - 1));
    if ('src' in hiddenSlide) {
      hiddenSlide.src = nextImg.src;
    }
    if ('alt' in hiddenSlide) {
      hiddenSlide.alt = nextImg.alt ?? '';
    }

    const tl = gsap.timeline({
      onComplete: () => {
        setCurrentPos(currentPosition => (currentPosition === 2 ? 0 : currentPosition + 1));
        animationCallback();
      }
    });
    tl.to(containerRef.current.querySelector(selectors.at(currentPosition)), { x: '-100% ' })
      .to(
        containerRef.current.querySelector(selectors.at(currentPosition + 1) ?? selectors[0]),
        { x: 0 },
        '<'
      )
      .to(
        containerRef.current.querySelector(selectors.at(currentPosition + 1) ?? selectors[0]),
        { scaleX: 1 },
        '<0.2'
      )
      .set(hiddenSlide, { x: '100%', scaleX: 2 });

    return () => {
      tl.kill();
    };
  }, [nextImg, currentImg, setCurrentPos]);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const tl = gsap.timeline();

    const currentElem = containerRef.current.querySelector('img.current-slide');
    if ('src' in currentElem) {
      currentElem.src = currentImg.src;
    }
    const nextElem = containerRef.current.querySelector('img.next-slide');
    if ('src' in nextElem) {
      nextElem.src = nextImg.src;
    }

    tl.set(currentElem, { x: 0 })
      .set(containerRef.current.querySelector('img.prev-slide'), { x: '-100%' })
      .set(nextElem, { x: '100%', scaleX: 2 });

    setHasRendered(true);

    return () => {
      tl.kill();
    };
  }, [setHasRendered]);

  return (
    <figure
      ref={containerRef}
      style={{
        ...positions,
        width: vwSizes,
        height: vwSizes,
        maxWidth: maxSizes,
        maxHeight: maxSizes,
        zIndex: 10 - index,
        transformStyle: 'preserve-3d'
      }}
      className="absolute top-1/2 -translate-y-1/2 overflow-hidden cursor-pointer bg-black rounded-lg border border-[#7C7C7C]"
      onClick={handleClick}
    >
      <img
        className="prev-slide object-cover w-full h-full absolute left-0 top-0 origin-left"
        src=""
        alt="prevImage"
        sizes="33vw"
        style={{ opacity }}
      />
      <img
        src=""
        alt="currentImage"
        className="relative current-slide object-cover w-full h-full origin-left"
        sizes="33vw"
        style={{ opacity }}
      />
      <img
        src=""
        alt="nextImage"
        className="next-slide object-cover w-full h-full absolute left-0 top-0 origin-left"
        sizes="33vw"
        style={{ opacity }}
      />
    </figure>
  );
};
